/**
 * Protected route (AuthBoundary pattern from web/src/auth/AuthBoundary.tsx).
 *
 * Protected admin routes must:
 *   1. wait for Clerk initialization;
 *   2. require a signed-in Clerk session (same Clerk instance as the customer
 *      dashboard);
 *   3. resolve the operator role via GET /v1/admin/me (see RequireRole).
 *
 * This component enforces steps 1–2: it never renders children while Clerk is
 * uninitialized and redirects signed-out visitors to /sign-in with the
 * attempted path preserved as a return path.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { LoadingState } from '@/components/feedback/LoadingState';

export function ProtectedRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  // Step 1: wait for Clerk initialization. Protected routes never render while
  // authentication is unresolved.
  if (!isLoaded) {
    return <LoadingState label="Loading session…" />;
  }

  // Step 2: signed-out users cannot access admin routes.
  if (!isSignedIn) {
    const params = new URLSearchParams({ redirect: location.pathname + location.search });
    return <Navigate to={`/sign-in?${params.toString()}`} replace />;
  }

  // Step 3 (operator role) is delegated to <RequireRole/> in the router.
  return <Outlet />;
}
